import React from "react";
import { NavigationDataType, NavigationProviderProps, NavigationProviderStateType, NavOptionType } from "./type";
import {
  getFilteredNavOptionByAuthority,
  getParentOptionIndexInHistory,
  isOptionAvailableInState,
  isPathExistInOption,
  setItemActiveByKey
} from "./util";
import { useLocation } from "react-router-dom";
import { useAuthority } from "context/authority";

const initialValue = {
  navHistory: [],
  navFuture: []
};

const Context = React.createContext<NavigationDataType>(initialValue);

function CustomNavigationProvider(props: NavigationProviderProps) {
  const location = useLocation();
  const { hasAnyAuthority } = useAuthority();
  const [state, setState] = React.useState<NavigationProviderStateType>({
    ...initialValue,
    navHistory: [],
    navFuture: []
  });

  const addNavOptions = React.useCallback(
    (navOption: NavOptionType) => {
      setState(data => {
        if (isOptionAvailableInState({ state: data, navOption })) {
          return data;
        }
        const navOptionFilteredByAuthority = getFilteredNavOptionByAuthority(navOption, hasAnyAuthority);
        if (!navOptionFilteredByAuthority) {
          return data;
        }
        const dataObj = {
          ...data,
          navHistory: [...data.navHistory],
          navFuture: [...data.navFuture]
        };
        if (
          dataObj.navHistory.length > 0 &&
          dataObj.navHistory[dataObj.navHistory.length - 1].key === navOptionFilteredByAuthority.key
        ) {
          dataObj.navHistory.pop();
        }
        dataObj.navFuture = [];
        if (dataObj.navHistory.length === 0) {
          dataObj.navHistory.push(navOptionFilteredByAuthority);
        } else {
          const parentOptionIndex = getParentOptionIndexInHistory(navOptionFilteredByAuthority, dataObj.navHistory);
          if (parentOptionIndex !== -1) {
            const indexToInsert = parentOptionIndex + 1;
            dataObj.navHistory.splice(
              indexToInsert,
              dataObj.navHistory.length - indexToInsert,
              navOptionFilteredByAuthority
            );
          }
        }
        return dataObj;
      });
    },
    [hasAnyAuthority]
  );

  const updateNavOptions = React.useCallback(
    (navOption: NavOptionType) => {
      if (!isOptionAvailableInState({ navOption, state })) {
        addNavOptions(navOption);
        return;
      }
      setState(data => {
        const dataObj = {
          ...data,
          navHistory: [...data.navHistory],
          navFuture: [...data.navFuture]
        };
        const historyIndex = dataObj.navHistory.findIndex(item => item.key === navOption.key);
        if (historyIndex > -1) {
          dataObj.navHistory[historyIndex] = navOption;
        } else {
          const futureIndex = dataObj.navFuture.findIndex(item => item.key === navOption.key);
          if (futureIndex > -1) {
            dataObj.navFuture[historyIndex] = navOption;
          }
        }
        return dataObj;
      });
    },
    [addNavOptions, state]
  );

  const removeNavOptions = React.useCallback((navOption: NavOptionType) => {
    setState(data => {
      const isExistInHistory = data.navHistory.find(i => i.key === navOption.key);
      const isExistInFuture = data.navFuture.find(i => i.key === navOption.key);
      if (!isExistInFuture && !isExistInHistory) {
        return data;
      }
      const dataObj = {
        ...data,
        navHistory: [...data.navHistory.filter(i => i.key !== navOption.key)],
        navFuture: [...data.navFuture.filter(i => i.key !== navOption.key)]
      };
      return dataObj;
    });
  }, []);

  const handleBackClick = React.useCallback(() => {
    setState(data => {
      const dataObj = { ...data, navHistory: [...data.navHistory], navFuture: [...data.navFuture] };
      const currItem = dataObj.navHistory.pop();
      currItem && dataObj.navFuture.push(currItem);
      return dataObj;
    });
  }, []);

  const onItemClick = React.useCallback((item: NavOptionType) => {
    setState(data => {
      const dataObj = { ...data, navHistory: [...data.navHistory], navFuture: [...data.navFuture] };
      if (dataObj.navFuture.length > 0 && dataObj.navFuture[dataObj.navFuture.length - 1].key === item.key) {
        const currItem = dataObj.navFuture.pop();
        currItem && dataObj.navHistory.push(currItem);
      } else {
        dataObj.navFuture = [];
      }
      setItemActiveByKey(dataObj.navHistory[dataObj.navHistory.length - 1], item.key);
      return dataObj;
    });
  }, []);

  const setApplicableOptionsBasedOnLocation = React.useCallback(
    path => {
      const index = [...state.navHistory].reverse().findIndex(item => isPathExistInOption(item, path));
      if (index !== -1 && index !== 0 && state.navHistory.length > 0) {
        const itemsCount = state.navHistory.length - index;
        setState({ ...state, navHistory: state.navHistory.slice(0, itemsCount) });
      }
    },
    [state]
  );

  React.useEffect(() => {
    const pathArr = location.pathname.split("/");
    const path = pathArr.length > 0 && pathArr[pathArr.length - 1];
    path && setApplicableOptionsBasedOnLocation(path);
  }, [setApplicableOptionsBasedOnLocation, location.pathname]);

  return (
    <Context.Provider
      value={{ ...state, addNavOptions, updateNavOptions, removeNavOptions, handleBackClick, onItemClick }}
      {...props}
    />
  );
}

function useCustomNavigation() {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error(`useNavigationContext must be used within a NavigationProvider`);
  }
  return context;
}

export { CustomNavigationProvider, useCustomNavigation };
